import { Component, Input } from '@angular/core';
import { FormBuilder, NgControl } from '@angular/forms';
import {
	FormConfig,
	CustomFormAccessor,
} from 'src/shared/form-models/form-cva.component';
import { buildArrayFromEnum } from 'src/shared/utilities/utility-functions';
import { JobNetworkOrServiceOrder, JobsClient } from 'src/shared/api';
import { displayNetworkOrServiceOrder } from 'src/shared/enums';
import {
	OptionalJobFieldsToShow,
	OptionalJobFields,
} from '../optional-fields.models';
import { ReplaySubject } from 'rxjs';
import { map, shareReplay, switchMap } from 'rxjs/operators';

const optionalJobFieldsFormConfig: FormConfig<OptionalJobFields> = {
	workOrderNumber: [''],
	projectNumber: [''],
	projectManagerName: [''],
	termsAndConditions: [''],
	isSupervisorJob: [''],
	networkOrServiceOrder: [''],
	isThirdPartyJob: [''],
	agreementNumber: [''],
	upFolderNumber: [''],
	nsActivityNumber: [''],
	protection: [''],
	referenceNumber: [''],
	requiresJobSetups: [''],
};

@Component({
	selector: 'rpfsa-optional-job-fields',
	templateUrl: './optional-job-fields.component.html',
})
export class OptionalJobFieldsComponent extends CustomFormAccessor<OptionalJobFields> {
	@Input()
	fieldsToShow: OptionalJobFieldsToShow = {} as OptionalJobFieldsToShow;

	private readonly _railroadId = new ReplaySubject<number>(1);

	@Input()
	set railroadId(input: number) {
		this._railroadId.next(input);
	}
	
	ngOnInit() {
 		this.setDefaultRequiresJobSetups();
	}

	private setDefaultRequiresJobSetups(): void {
  		const control = this.form.get('requiresJobSetups');
		const hasValue = control?.value === true || control?.value === false;

	    if (this.fieldsToShow?.showRequiresJobSetups && !hasValue) {
    		control?.setValue(true);
  		}
	}

	readonly autocompleteValuesForOptionalFields$ = this._railroadId.pipe(
		switchMap((railroadId) =>
			this.jobsClient.getAutocompleteValuesForOptionalFields(railroadId)
		),
		shareReplay(1)
	);

	readonly filteredWorkOrderNumbers$ = this.form
		.get('workOrderNumber')
		.valueChanges.pipe(
			switchMap((input) =>
				this.autocompleteValuesForOptionalFields$.pipe(
					map((values) =>
						values.workOrderNumber.filter((v) => v.includes(input))
					)
				)
			)
		);

	readonly filteredProjectNumbers$ = this.form
		.get('projectNumber')
		.valueChanges.pipe(
			switchMap((input) =>
				this.autocompleteValuesForOptionalFields$.pipe(
					map((values) =>
						values.projectNumber.filter((v) => v.includes(input))
					)
				)
			)
		);

	readonly filteredProjectManagerNames$ = this.form
		.get('projectManagerName')
		.valueChanges.pipe(
			switchMap((input) =>
				this.autocompleteValuesForOptionalFields$.pipe(
					map((values) =>
						values.projectManagerName.filter((v) =>
							v.includes(input)
						)
					)
				)
			)
		);

	readonly filteredAgreementNumbers$ = this.form
		.get('agreementNumber')
		.valueChanges.pipe(
			switchMap((input) =>
				this.autocompleteValuesForOptionalFields$.pipe(
					map((values) =>
						values.agreementNumber.filter((v) => v.includes(input))
					)
				)
			)
		);

	readonly filteredUpFolderNumbers$ = this.form
		.get('upFolderNumber')
		.valueChanges.pipe(
			switchMap((input) =>
				this.autocompleteValuesForOptionalFields$.pipe(
					map((values) =>
						values.upFolderNumber.filter((v) => v.includes(input))
					)
				)
			)
		);

	readonly filterReferenceNumbers$ = this.form
		.get('referenceNumber')
		.valueChanges.pipe(
			switchMap((input) =>
				this.autocompleteValuesForOptionalFields$.pipe(
					map((values) =>
						values.referenceNumber.filter((v) => v.includes(input))
					)
				)
			)
		);

	readonly filteredNsActivityNumbers$ = this.form
		.get('nsActivityNumber')
		.valueChanges.pipe(
			switchMap((input) =>
				this.autocompleteValuesForOptionalFields$.pipe(
					map((values) =>
						values.nsActivityNumber.filter((v) => v.includes(input))
					)
				)
			)
		);

	readonly networkOrServiceOrderTypes = buildArrayFromEnum(
		JobNetworkOrServiceOrder
	);
	readonly displayNetworkOrServiceOrder = displayNetworkOrServiceOrder;

	constructor(
		public ngControl: NgControl,
		public fb: FormBuilder,
		public jobsClient: JobsClient
	) {
		super(ngControl, fb, optionalJobFieldsFormConfig);
	}
}
